import classNames from "classnames";
import React, { useId } from "react";
import Select, { components, MenuPlacement } from "react-select";
import { FilterOptionOption } from "react-select/dist/declarations/src/filters";
import style from "../../scss/export.module.scss";
import { ChevronDownIcon, CloseCircleIcon } from "../Icons";
import "./searchable-select.scss";

export type TSearchableSelectOption = {
    label: string;
    value: string;
    value2?: string;
    value3?: string;
    disabled?: boolean;
};

export type TSearchableSelectSelectedOption = TSearchableSelectOption | null;

type TSearchableSelect = {
    label?: string;
    value: TSearchableSelectSelectedOption;
    options: TSearchableSelectOption[];
    onChange?: (arg0: TSearchableSelectSelectedOption) => void;
    placeholder?: string;
    noOptionsMessage?: string;
    classname?: string;
    disabled?: boolean;
    errorMessage?: string;
    hasError?: boolean;
    isClearable?: boolean;
    smallHeight?: boolean;
    onMenuOpen?: () => void;
    onMenuClose?: () => void;
    filterOption?: (option: FilterOptionOption<TSearchableSelectOption>, inputValue: string) => boolean;
    customKey?: number | string;
    closeMenuOnSelect?: boolean;
    menuPlacement?: MenuPlacement;
    ariaLabel?: string;
};

export const SearchableSelect = ({
    label,
    value,
    options,
    onChange,
    placeholder = "",
    noOptionsMessage = "",
    classname = "",
    disabled,
    errorMessage,
    hasError,
    isClearable = true,
    smallHeight = false,
    onMenuOpen,
    onMenuClose,
    filterOption,
    customKey,
    closeMenuOnSelect = true,
    menuPlacement = "bottom",
    ariaLabel,
}: TSearchableSelect) => {
    const labelId = useId();

    return (
        <>
            {label && (
                <label id={labelId} className="c-searchable-select__label">
                    {label}
                </label>
            )}
            <Select
                key={customKey}
                value={value}
                options={options}
                onChange={onChange}
                isClearable={isClearable}
                className={classNames(`c-searchable-select ${classname}`, {
                    "c-searchable-select--small-height": smallHeight,
                    "c-searchable-select--error": hasError,
                })}
                classNamePrefix="c-searchable-select"
                components={{ DropdownIndicator, ClearIndicator }}
                placeholder={placeholder}
                noOptionsMessage={() => noOptionsMessage}
                isDisabled={disabled}
                onMenuOpen={onMenuOpen}
                onMenuClose={onMenuClose}
                isOptionDisabled={option => !!option.disabled}
                filterOption={filterOption}
                closeMenuOnSelect={closeMenuOnSelect}
                menuPlacement={menuPlacement}
                getOptionValue={option => `${option.label} ${option.value} ${option.value2} ${option.value3}`}
                menuShouldScrollIntoView
                aria-invalid={hasError}
                aria-label={ariaLabel}
                aria-labelledby={label ? labelId : undefined}
                aria-errormessage={errorMessage}
            />
            {hasError && errorMessage && (
                <p className="c-searchable-select__error-message">{hasError && errorMessage}</p>
            )}
        </>
    );
};

const DropdownIndicator = (props: any) => {
    return (
        <components.DropdownIndicator {...props}>
            <ChevronDownIcon color={style.blackColor} size={18} />
        </components.DropdownIndicator>
    );
};

const ClearIndicator = (props: any) => {
    return (
        <components.ClearIndicator {...props}>
            <CloseCircleIcon color={style.blackColor} size={18} />
        </components.ClearIndicator>
    );
};
